import { useMemo } from "react";
import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  ICellRendererParams,
  ValueGetterParams
} from "ag-grid-enterprise";
import { useParams } from "react-router";
import {
  CellValueRendererWithEditIcon,
  nextDeadlineCellRenderer
} from "components/cell-renders";
import { DateUtils } from "utils/dateutils";
import CustomDateCellEditor from "components/cell-editor/date-cell-editor";
import { EWorkflowStatusDataBlock } from "constants/index";
import {
  featureStatusCellRenderer,
  twoRowCellRenderer,
  twoRowGetQuickFilterText
} from "../../../entity-app/shared-components/log-render-components/cell-renders";
import {
  coldefHiddenField,
  coldefNavigation,
  coldefSingleField,
  coldefUser,
  coldefDateField
} from "../../../entity-app/shared-components/log-render-components/col-defs";

export const useDesignPackageAgGrid = (props: {
  featureKey: string;
  milestonesColumns: any;
  designPackageHeader: any;
  cannotWriteAccessOfDesignPackage: boolean;
}) => {
  const {
    featureKey,
    milestonesColumns,
    designPackageHeader,
    cannotWriteAccessOfDesignPackage
  } = props;
  const { projectId } = useParams() as any;

  const agGridCheckFieldEditable = (params: CellClassParams<any, any>) => {
    const { data, colDef } = params;
    const colId = colDef.colId || "";

    const fieldEditWhenNotComplete = ["manual_entry_date"];
    if (
      fieldEditWhenNotComplete.includes(colId) &&
      data.workflow_status === EWorkflowStatusDataBlock.WF_COMPLETED
    )
      return false;
    return true;
  };

  const isGridCellEditable = (params: CellClassParams<any, any>) => {
    return (
      !cannotWriteAccessOfDesignPackage &&
      params?.colDef?.editable &&
      agGridCheckFieldEditable(params)
    );
  };

  const defaultColDef = useMemo(
    () =>
      ({
        sortable: true,
        editable: false,
        filter: true,
        resizable: true,
        menuTabs: ["filterMenuTab"],
        minWidth: 100,
        cellRenderer: (params: ICellRendererParams) => {
          return CellValueRendererWithEditIcon(params, true);
        },
        cellEditorPopup: false
      } as ColDef<any>),
    []
  );

  const columnDefs = useMemo(
    () =>
      [
        coldefNavigation(
          {
            field: "auto_inc_value",
            headerName: designPackageHeader?.id,
            pinned: "left",
            lockPosition: "left",
            initialWidth: 100,
            sort: "asc",
            lockVisible: true
          },
          projectId,
          featureKey
        ),
        coldefSingleField({
          field: "number",
          headerName: designPackageHeader?.number,
          pinned: "left",
          lockPosition: "left",
          width: 120,
          editable: true
        }),
        coldefSingleField({
          field: "title",
          headerName: designPackageHeader?.title,
          width: 220,
          pinned: "left",
          lockPosition: "left",
          cellRenderer: twoRowCellRenderer,
          cellRendererParams: {
            featureKey,
            projectId
          },
          getQuickFilterText: twoRowGetQuickFilterText,
          editable: true,
          lockVisible: true
        }),
        coldefHiddenField({
          field: "description",
          headerName: designPackageHeader?.description,
          pinned: "left",
          lockPosition: "left"
        }),
        coldefSingleField({
          field: "milestone_state",
          headerName: designPackageHeader?.workflow_status,
          lockVisible: true,
          width: 200,
          minWidth: 200,
          tooltipField: undefined,
          tooltipValueGetter: undefined,
          editable: true,
          cellRenderer: featureStatusCellRenderer
        }),
        coldefDateField({
          field: "planned_next_milestone_date",
          headerName: designPackageHeader?.planned_next_milestone_date,
          width: 160,
          valueGetter: (params: ValueGetterParams) => {
            return DateUtils.format(params.data?.planned_next_milestone_date);
          },
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefDateField({
          field: "projected_next_milestone_date",
          headerName: designPackageHeader?.projected_next_milestone_date,
          width: 160,
          valueGetter: (params: ValueGetterParams) => {
            return DateUtils.format(params.data?.projected_next_milestone_date);
          },
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefDateField({
          colId: "manual_entry_date",
          field: "manual_entry_date",
          headerName: designPackageHeader?.manual_entry_date || "Deadline Date",
          editable: true,
          cellRenderer: CellValueRendererWithEditIcon,
          cellEditor: CustomDateCellEditor,
          valueGetter: undefined,
          valueFormatter: ({ value }) => (value ? DateUtils.format(value) : ""),
          cellEditorParams: {
            placeholder: `Select ${
              designPackageHeader?.manual_entry_date || "Deadline Date"
            }`
          }
        }),
        coldefDateField({
          field: "final_deadline_date",
          colId: "final_deadline_date",
          headerName: designPackageHeader?.final_deadline_date || "Deadline",
          headerTooltip: designPackageHeader?.final_deadline_date || "Deadline",
          valueGetter: (params: ValueGetterParams) => {
            return DateUtils.format(params.data.final_deadline_date);
          },
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefDateField({
          field: "projected_last_milestone_date",
          colId: "projected_last_milestone_date",
          headerName:
            designPackageHeader?.projected_last_milestone_date ||
            "Projected Completion Date",
          headerTooltip:
            designPackageHeader?.projected_last_milestone_date ||
            "Projected Completion Date",
          valueGetter: (params: ValueGetterParams) => {
            return DateUtils.format(params.data.projected_last_milestone_date);
          },
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefUser({
          def: {
            headerName:
              designPackageHeader?.responsible_designer_id ||
              "RESPONSIBLE DESIGN FIRM",
            editable: true
          },
          user: {
            id: "design_firm_id",
            firstName: "design_firm_name",
            lastName: "",
            dropdownType: "RDF_dropdown"
          }
        }),
        coldefUser({
          def: {
            headerName: designPackageHeader?.design_firm_id,
            editable: true
          },
          user: {
            id: "responsible_designer_id",
            firstName: "responsible_designer_first_name",
            lastName: "responsible_designer_last_name",
            statusId: "responsible_designer_status_id",
            dropdownType: "RD_dropdown",
            dependentId: "design_firm_id",
            dependentNotFoundContent: "First select a Responsible Design Firm"
          }
        }),
        coldefUser({
          def: {
            headerName: designPackageHeader?.gc_representative_id,
            editable: true
          },
          user: {
            id: "gc_representative_id",
            firstName: "gc_representative_first_name",
            lastName: "gc_representative_last_name"
          }
        }),
        ...milestonesColumns.allColumns
      ] as (ColDef<any> | ColGroupDef<any>)[],
    [projectId, featureKey, milestonesColumns.allColumns, designPackageHeader]
  );
  return {
    columnDefs,
    defaultColDef,
    milestonesColumns,
    isGridCellEditable
  };
};
